import i18n from '@/plugins/i18n';

const BackgroundColor = Object.freeze({
  LIGHT: 'rgb(255, 255, 255)',
  DARK: 'rgb(255, 255, 255)',
});
const FontColor = Object.freeze({
  LIGHT: 'rgb(255, 255, 255)',
  DARK: 'rgb(255, 255, 255)',
  DARKOP: 'rgba(255, 255, 255,0.3)',
});
const FontFamily = Object.freeze({
  REGULAR: 'RakutenSansUI-Regular',
  SEMIBOLD: 'RakutenSansUI-SemiBold',
  BOLD: 'RakutenSansUI-Bold',
});

const Markers = Object.freeze({
  size: [4, 4],
  colors: undefined,
  strokeColors: 'rgb(30,32,33)',
  strokeWidth: 1,
  strokeOpacity: 1,
  strokeDashArray: 0,
  fillOpacity: 1,
  shape: 'circle',
  radius: 2,
  offsetX: 0,
  offsetY: 0,
  onClick: undefined,
  onDblClick: undefined,
  showNullDataPoints: true,
  hover: {
    size: undefined,
    sizeOffset: 3,
  },
});

const Legend = Object.freeze({
  show: true,
  showForSingleSeries: false,
  showForNullSeries: true,
  showForZeroSeries: true,
  position: 'bottom',
  horizontalAlign: 'center',
  floating: false,
  fontSize: '14px',
  fontFamily: FontFamily.REGULAR,
  fontWeight: 400,
  formatter: undefined,
  inverseOrder: false,
  width: undefined,
  height: 50,
  tooltipHoverFormatter: undefined,
  offsetX: 0,
  offsetY: 0,
  labels: {
    colors: FontColor.DARKOP,
    useSeriesColors: false,
  },
  markers: {
    width: 12,
    height: 12,
    strokeWidth: 0,
    strokeColor: BackgroundColor.DARK,
    fillColors: undefined,
    radius: 12,
    customHTML: undefined,
    onClick: undefined,
    offsetX: 0,
    offsetY: 0,
  },
  itemMargin: {
    horizontal: 5,
    vertical: 0,
  },
  onItemClick: {
    toggleDataSeries: true,
  },
  onItemHover: {
    highlightDataSeries: true,
  },
});

const ChartToolBar = Object.freeze({
  show: false,
  tools: {
    download: false,
    selection: true,
    zoom: true,
    zoomin: true,
    zoomout: true,
    pan: true,
    reset: true | '<img src="@/assets/icons/reset.svg" width="20">',
    customIcons: [],
  },
});
const Animations = Object.freeze({
  enabled: true,
  easing: 'easeinout',
  speed: 800,
  animateGradually: {
    enabled: true,
    delay: 150,
  },
  dynamicAnimation: {
    enabled: true,
    speed: 350,
  },
});
const ToolTip = Object.freeze({
  enabled: true,
  enabledOnSeries: undefined,
  shared: true,
  followCursor: false,
  intersect: false,
  inverseOrder: false,
  custom: undefined,
  fillSeriesColor: false,
  theme: 'dark',
  style: {
    fontSize: '12px',
    fontFamily: undefined,
  },

  onDatasetHover: {
    highlightDataSeries: false,
  },
  y: {
    show: true,

    title: {
      formatter: (seriesName) => '',
    },
  },
  z: {
    formatter: undefined,
    title: 'Size: ',
  },
  marker: {
    show: false,
  },
  items: {
    display: 'flex',
  },
  fixed: {
    enabled: false,
    position: 'topRight',
    offsetX: 0,
    offsetY: 0,
  },
});

const MultiLineToolTip = Object.freeze({
  ...ToolTip,
  y: {
    show: true,

    title: {
      formatter: (seriesName) => seriesName,
    },
  },
});

const ChartLablesStyles = Object.freeze({
  color: FontColor.LIGHT,
  fontSize: '0.875em',
  fontFamily: FontFamily.REGULAR,
  fontWeight: 0,
  cssClass: 'chart-lables',
});

const AxisLabels = Object.freeze({
  x: {
    show: true,
    minWidth: 8,
    rotate: 0,
    hideOverlappingLabels: true,
    style: {
      colors: FontColor.LIGHT,
      fontSize: '0.75em',
      fontFamily: FontFamily.REGULAR,
      fontWeight: 400,
      cssClass: 'apexcharts-xaxis-label',
    },
    offsetX: 0,
    offsetY: 0,
  },
  y: {
    show: true,
    minWidth: 10,
    style: {
      colors: [],
      fontSize: '0.75em',
      fontFamily: 'RakutenSansUI-Regular',
      fontWeight: 400,
      cssClass: 'apexcharts-yaxis-label',
    },
    offsetX: 0,
    offsetY: 0,
    rotate: 0,
  },
});
const Grid = Object.freeze({
  show: true,
  borderColor: FontColor.DARKOP,
  strokeDashArray: 0,
  position: 'back',
  xaxis: {
    lines: {
      show: false,
    },
  },
  yaxis: {
    lines: {
      show: true,
    },
  },
});
const XAxisConfig = Object.freeze({
  tooltip: {
    enabled: false,
    offsetX: 0,
  },
  title: {
    offsetX: 0,
    offsetY: 10,
    style: ChartLablesStyles,
  },
  axisTicks: {
    show: true,
    borderType: 'solid',
    color: FontColor.DARK,
    height: 10,
    offsetX: 0,
    offsetY: 0,
  },
  labels: {
    show: true,
    minWidth: 8,
    rotate: 0,
    hideOverlappingLabels: true,
    style: {
      colors: FontColor.LIGHT,
      fontSize: '0.75em',
      fontFamily: FontFamily.REGULAR,
      fontWeight: 400,
      cssClass: 'apexcharts-xaxis-label',
    },
    offsetX: 0,
    offsetY: 0,
  },
});
const YAxisConfig = Object.freeze({
  show: true,
  showAlways: true,
  showForNullSeries: true,
  seriesName: undefined,
  opposite: false,
  reversed: false,
  logarithmic: false,
  tickAmount: 5,
  tooltip: {
    enabled: false,
    offsetX: 0,
  },
  axisTicks: {
    show: false,
    borderType: 'solid',
    color: FontColor.DARKOP,
    width: 6,
    offsetX: 0,
    offsetY: 0,
  },
  axisBorder: {
    show: false,
    color: FontColor.DARKOP,
    offsetX: 0,
    offsetY: 0,
  },
  crosshairs: {
    show: false,
    position: 'back',
    stroke: {
      color: '#b6b6b6',
      width: 1,
      dashArray: 0,
    },
  },
  title: {
    align: 'left',
    rotate: -90,
    offsetX: 0,
    offsetY: 20,
    style: {
      color: FontColor.LIGHT,
      fontSize: '0.875em',
      fontFamily: FontFamily.REGULAR,
      fontWeight: 0,
      cssClass: 'chart-lables',
    },
  },
  labels: {
    show: true,

    minWidth: 10,
    style: {
      colors: FontColor.LIGHT,
      fontSize: '0.75em',
      fontFamily: FontFamily.REGULAR,
      fontWeight: 400,
      cssClass: 'apexcharts-yaxis-label',
    },
    offsetX: 0,
    offsetY: 0,
    rotate: 0,
  },
});

const ChartFill = Object.freeze({
  type: 'gradient',
  gradient: {
    shade: 'light',
    type: 'horizontal',
    shadeIntensity: 0.25,
    gradientToColors: undefined,
    inverseColors: true,
    opacityFrom: 0.85,
    opacityTo: 0.85,
    stops: [50, 0, 100],
  },
});

const tooltipValues = {
  enabled: true,
  enabledOnSeries: true,
  shared: true,
  followCursor: false,
  intersect: false,
  inverseOrder: false,
  custom: undefined,
  fillSeriesColor: false,
  theme: 'dark',
  style: {
    fontSize: '12px',
    fontFamily: undefined,
  },
  onDatasetHover: {
    highlightDataSeries: false,
  },
  x: {
    show: true,
    format: 'dd MMM',
    formatter: undefined,
  },
  y: {
    formatter: undefined,
    title: {
      formatter: (seriesName) => seriesName,
    },
  },
  z: {
    formatter: undefined,
    title: 'Size: ',
  },
  marker: {
    show: true,
  },
  items: {
    display: 'flex',
  },
  fixed: {
    enabled: false,
    position: 'topRight',
    offsetX: 0,
    offsetY: 0,
  },
};

const getCustomTooltipContent = (
  series,
  data,
  intervalTime,
  compareIntervalTime,
  item,
  dataPointIndex,
  w,
  capability,
  title,
) => {
  const hiddenSeries = new Set([
    ...w.globals.collapsedSeriesIndices,
    ...w.globals.ancillaryCollapsedSeriesIndices,
  ]);
  const currIntervalPoint =
    intervalTime[dataPointIndex]?.replace(/\n/g, ' ') ?? 'N/A';
  const compareIntervalPoint =
    compareIntervalTime[dataPointIndex]?.replace(/\n/g, ' ') ?? 'N/A';
  const current = series[0]?.[dataPointIndex] ?? 'N/A';
  const previous = series[1]?.[dataPointIndex] ?? 'N/A';
  const difference = Math.abs(current - previous);
  const delta =
    hiddenSeries.has(0) ||
    hiddenSeries.has(1) ||
    current === 'N/A' ||
    previous === 'N/A'
      ? 'N/A'
      : difference % 1 === 0
        ? difference
        : difference.toFixed(2);
  const showArrow = delta !== 'N/A' && delta !== 0;
  const arrow = showArrow ? (current > previous ? '↑' : '↓') : '';
  const arrowColor = showArrow ? (current > previous ? 'green' : 'red') : '';

  const isK8M = capability === 'K8M';
  const headerTitle = isK8M ? title : item?.header?.title;
  const seriesColor = w.config?.colors;
  let tooltipContent = `<div class="custom-title">${headerTitle}</div>`;
  if (!item.isMultiLineSeriesTooltip) {
    tooltipContent = `<div class="custom-title">${headerTitle}<span class="tooltip-delta" style="color: ${arrowColor};">${arrow} ${delta}</span></div>`;
  }
  let currentPeriodContent = '<div class="tooltip-section">';
  let previousPeriodContent = '<div class="tooltip-section">';

  let nonComparedSeries = [];
  let comparedSeries = [];

  if (isK8M) {
    const keys = data.map((item) => item.label);
    nonComparedSeries = keys.filter((key) => !key.includes('Previous'));
    comparedSeries = keys.filter((key) => key.includes('Previous'));
  } else {
    const keys = Object.keys(data);
    nonComparedSeries = keys.filter((key) => !key.includes('compared'));
    comparedSeries = keys.filter((key) => key.includes('compared'));
  }
  let adjustedIndex = 0;
  nonComparedSeries.forEach((key, index) => {
    const currentIndex = isK8M ? adjustedIndex : index;
    const current = series[currentIndex]?.[dataPointIndex] ?? 'N/A';

    if (!hiddenSeries.has(currentIndex)) {
      currentPeriodContent +=
        '<div class="tooltip-series-group">' +
        '<span class="tooltip-marker" style="background-color: ' +
        seriesColor[currentIndex] +
        '"></span>' +
        '<span class="series-tooltip-font-group">' +
        (isK8M ? '' : i18n.global.t('currentPeriod') + ' - ') +
        (!item.isMultiLineSeriesTooltip ? '' : key + ', ') +
        currIntervalPoint +
        ': ' +
        current +
        '</span>' +
        '<br>' +
        '</div>';
    }
    adjustedIndex += 2;
  });
  let comparedIndex = isK8M ? 1 : Math.floor(series.length / 2);
  comparedSeries.forEach((key, index) => {
    const previousIndex = comparedIndex;

    const previous = Number.isNaN(series[previousIndex]?.[dataPointIndex])
      ? 'N/A'
      : series[previousIndex]?.[dataPointIndex] ?? 'N/A';
    if (!hiddenSeries.has(previousIndex)) {
      previousPeriodContent +=
        '<div class="tooltip-series-group">' +
        '<span class="legend-dashed-circle" style="border-color: ' +
        (isK8M ? seriesColor[previousIndex] : seriesColor[index]) +
        '"></span>' +
        '<span class="series-tooltip-group">' +
        (isK8M ? '' : i18n.global.t('previousPeriod') + ' - ') +
        (!item.isMultiLineSeriesTooltip ? '' : key.split('_compared')) +
        ' ' +
        compareIntervalPoint +
        ': ' +
        previous +
        '</span>' +
        '<br>' +
        '</div>';
    }
    comparedIndex += isK8M ? 2 : 1;
  });
  currentPeriodContent += '</div>';
  previousPeriodContent += '</div>';
  tooltipContent += currentPeriodContent + previousPeriodContent;

  return tooltipContent;
};

const dashLinesize = 10;
const lineWidth = 3;

export {
  BackgroundColor,
  FontColor,
  FontFamily,
  ToolTip,
  Markers,
  Legend,
  ChartToolBar,
  Animations,
  ChartLablesStyles,
  AxisLabels,
  XAxisConfig,
  YAxisConfig,
  ChartFill,
  Grid,
  MultiLineToolTip,
  dashLinesize,
  lineWidth,
  tooltipValues,
  getCustomTooltipContent,
};
